"use client"

import { Row, Table, TableBody, TableHeader } from "@/app/components/table"
import { type Ref, forwardRef, memo, useCallback } from "react"
import { TableVirtuoso, type TableVirtuosoHandle } from "react-virtuoso"
import type { LeaderboardItem } from "../../store.reatom"
import { getColumns } from "./columns/columns"
import { HeaderContent } from "./template"

interface Props {
  leaderboardItems: LeaderboardItem[]
  isHolding: boolean
  showBlockchain: boolean
  hideFloorChart: boolean
}

// Cache for columns to avoid recreating them
const columnsCache = new Map<string, ReturnType<typeof getColumns>>()

function getCachedColumns(
  isHolding: boolean,
  showBlockchain: boolean,
  hideFloorChart: boolean,
) {
  const key = `${isHolding}-${showBlockchain}-${hideFloorChart}`
  if (!columnsCache.has(key)) {
    columnsCache.set(key, getColumns(isHolding, showBlockchain, hideFloorChart))
  }
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  return columnsCache.get(key)!
}

const ItemContent = memo(function ItemContent({
  item,
  isHolding,
  showBlockchain,
  hideFloorChart,
}: {
  item: LeaderboardItem
  isHolding: boolean
  showBlockchain: boolean
  hideFloorChart: boolean
}) {
  const columns = getCachedColumns(isHolding, showBlockchain, hideFloorChart)

  return (
    <>
      {columns.map((column) => (
        <column.Cell key={column.type}>
          <column.Content {...item} />
        </column.Cell>
      ))}
    </>
  )
})

export const LeaderboardTable = forwardRef(function LeaderboardTable(
  props: Props,
  ref: Ref<TableVirtuosoHandle>,
) {
  const { leaderboardItems, isHolding, showBlockchain, hideFloorChart } = props

  const itemContent = useCallback(
    (_: number, item: LeaderboardItem) => (
      <ItemContent
        item={item}
        isHolding={isHolding}
        showBlockchain={showBlockchain}
        hideFloorChart={hideFloorChart}
      />
    ),
    [isHolding, showBlockchain, hideFloorChart],
  )

  return (
    <TableVirtuoso
      ref={ref}
      components={{
        Table,
        TableHead: LeaderboardTableHeader,
        TableRow: Row,
        TableBody,
      }}
      computeItemKey={(_, item) => item.id}
      initialItemCount={Math.min(10, leaderboardItems.length)}
      data={leaderboardItems}
      fixedHeaderContent={() => (
        <HeaderContent hideFloorChart={hideFloorChart} />
      )}
      itemContent={itemContent}
      useWindowScroll
    />
  )
})

function LeaderboardTableHeader({
  className,
  ...props
}: React.ComponentProps<typeof TableHeader>) {
  return <TableHeader className={className} {...props} />
}
